import { useState, useEffect, useRef } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { X, Database, ChevronRight, CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react'
import {
  startStarrocksOnboard,
  startStarrocksExport,
  getStarrocksJobStatus,
  getSetupDefaults,
} from '../api/accounts'
import type { WarehouseJobResponse, StarrocksOnboardRequest, StarrocksExportRequest } from '../types'

interface Props {
  accId: number
  accountName: string
  onClose: () => void
}

type Step = 1 | 2
type WarehouseType = 'bigquery' | 'snowflake'

const POLL_INTERVAL = 3000

export function StarrocksSetupWizard({ accId, accountName, onClose }: Props) {
  const [step, setStep] = useState<Step>(1)

  // ── Step 1 state ────────────────────────────────────────────────────────────
  const [srHost, setSrHost] = useState('gcp-starrocks-db.fhpro.in')
  const [srPort, setSrPort] = useState(9030)
  const [srUser, setSrUser] = useState('admin')
  const [srPassword, setSrPassword] = useState('')
  const [sector, setSector] = useState('')

  // Step 1 job
  const [onboardJobId, setOnboardJobId] = useState<string | null>(null)
  const [onboardJob, setOnboardJob] = useState<WarehouseJobResponse | null>(null)
  const onboardRunning = onboardJob?.status === 'RUNNING'

  // ── Step 2 state ────────────────────────────────────────────────────────────
  const [warehouseType, setWarehouseType] = useState<WarehouseType>('bigquery')
  const [gcsPath, setGcsPath] = useState('gs://fhin-test-starrocks/import')
  // BigQuery
  const [bqProject, setBqProject] = useState('spark-bigquery-poc')
  const [bqDataset, setBqDataset] = useState('')
  // Snowflake
  const [sfAccount, setSfAccount] = useState('')
  const [sfUser, setSfUser] = useState('')
  const [sfPatToken, setSfPatToken] = useState('')
  const [sfDatabase, setSfDatabase] = useState('')
  const [sfSchema, setSfSchema] = useState('')
  const [sfStage, setSfStage] = useState('')

  // Step 2 job
  const [exportJobId, setExportJobId] = useState<string | null>(null)
  const [exportJob, setExportJob] = useState<WarehouseJobResponse | null>(null)
  const exportRunning = exportJob?.status === 'RUNNING'

  // ── Log scroll ref ─────────────────────────────────────────────────────────
  const logRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight
    }
  }, [onboardJob?.logLines, exportJob?.logLines])

  // ── Load defaults ───────────────────────────────────────────────────────────
  const { data: defaults } = useQuery({
    queryKey: ['setup-defaults'],
    queryFn: getSetupDefaults,
    staleTime: 5 * 60 * 1000,
  })
  useEffect(() => {
    if (!defaults?.starrocks) return
    setSrHost(defaults.starrocks.host || 'gcp-starrocks-db.fhpro.in')
    setSrPort(defaults.starrocks.port || 9030)
    setSrUser(defaults.starrocks.user || 'admin')
  }, [defaults])

  // ── Step 1: onboard mutation ────────────────────────────────────────────────
  const onboardMutation = useMutation({
    mutationFn: (req: StarrocksOnboardRequest) => startStarrocksOnboard(accId, req),
    onSuccess: (job) => {
      setOnboardJobId(job.jobId)
      setOnboardJob(job)
    },
  })

  // ── Step 1: poll ────────────────────────────────────────────────────────────
  const { data: polledOnboardJob } = useQuery({
    queryKey: ['sr-onboard-status', onboardJobId],
    queryFn: () => getStarrocksJobStatus(accId, onboardJobId!),
    enabled: !!onboardJobId && onboardRunning,
    refetchInterval: onboardRunning ? POLL_INTERVAL : false,
  })
  useEffect(() => {
    if (polledOnboardJob) setOnboardJob(polledOnboardJob)
  }, [polledOnboardJob])

  // ── Step 2: export mutation ─────────────────────────────────────────────────
  const exportMutation = useMutation({
    mutationFn: (req: StarrocksExportRequest) => startStarrocksExport(accId, req),
    onSuccess: (job) => {
      setExportJobId(job.jobId)
      setExportJob(job)
    },
  })

  // ── Step 2: poll ────────────────────────────────────────────────────────────
  const { data: polledExportJob } = useQuery({
    queryKey: ['sr-export-status', exportJobId],
    queryFn: () => getStarrocksJobStatus(accId, exportJobId!),
    enabled: !!exportJobId && exportRunning,
    refetchInterval: exportRunning ? POLL_INTERVAL : false,
  })
  useEffect(() => {
    if (polledExportJob) setExportJob(polledExportJob)
  }, [polledExportJob])

  // ── Handlers ────────────────────────────────────────────────────────────────
  function handleOnboard() {
    onboardMutation.mutate({
      host: srHost, port: srPort, user: srUser, password: srPassword,
      sector: sector || undefined,
    })
  }

  function handleExport() {
    const req: StarrocksExportRequest = {
      host: srHost,
      port: srPort,
      user: srUser,
      password: srPassword,
      warehouseType,
      gcsPath,
      sector: sector || undefined,
      ...(warehouseType === 'bigquery'
        ? { bqProject: bqProject || undefined, bqDataset: bqDataset || undefined }
        : {
            sfAccount: sfAccount || undefined,
            sfUser: sfUser || undefined,
            sfPatToken: sfPatToken || undefined,
            sfDatabase: sfDatabase || undefined,
            sfSchema: sfSchema || undefined,
            sfStage: sfStage || undefined,
          }),
    }
    exportMutation.mutate(req)
  }

  const step1Done = onboardJob?.status === 'COMPLETED'
  const step1Failed = onboardJob?.status === 'FAILED'
  const step2Done = exportJob?.status === 'COMPLETED'
  const step2Failed = exportJob?.status === 'FAILED'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Database className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">StarRocks Setup</h2>
              <p className="text-xs text-gray-500">{accountName} · acc_{accId}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {([
            { n: 1, label: 'Onboard' },
            { n: 2, label: 'Backfill Data' },
          ] as { n: Step; label: string }[]).map(({ n, label }) => (
            <button
              key={n}
              onClick={() => !onboardRunning && !exportRunning && setStep(n)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                step === n
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className={`mr-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
                step === n ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'
              }`}>{n}</span>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* ── Step 1: Onboard ─────────────────────────────────────────────── */}
          {step === 1 && (
            <>
              <p className="text-sm text-gray-500">
                Creates the <code className="text-xs bg-gray-100 px-1 rounded">acc_{accId}</code> database
                in StarRocks with all tables, materialized views, and views.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Host</label>
                  <input
                    value={srHost}
                    onChange={(e) => setSrHost(e.target.value)}
                    placeholder="gcp-starrocks-db.fhpro.in"
                    disabled={onboardRunning}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Port</label>
                  <input
                    type="number"
                    value={srPort}
                    onChange={(e) => setSrPort(Number(e.target.value))}
                    disabled={onboardRunning}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">User</label>
                  <input
                    value={srUser}
                    onChange={(e) => setSrUser(e.target.value)}
                    disabled={onboardRunning}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={srPassword}
                    onChange={(e) => setSrPassword(e.target.value)}
                    disabled={onboardRunning}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Sector <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <select
                    value={sector}
                    onChange={(e) => setSector(e.target.value)}
                    disabled={onboardRunning}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 bg-white"
                  >
                    <option value="">— None (core tables only) —</option>
                    <option value="insurance">Insurance</option>
                    <option value="loyalty">Loyalty</option>
                  </select>
                </div>
              </div>

              {/* Job log */}
              {onboardJob && (
                <JobLog job={onboardJob} logRef={logRef} />
              )}

              <div className="flex justify-between items-center">
                <StatusBadge job={onboardJob} isSubmitting={onboardMutation.isPending} />
                <div className="flex gap-3">
                  {step1Done && (
                    <button
                      onClick={() => setStep(2)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600"
                    >
                      Next: Backfill Data
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                  {!onboardJob && (
                    <button
                      onClick={handleOnboard}
                      disabled={!srHost || !srPassword || onboardMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50"
                    >
                      {onboardMutation.isPending ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Starting…</>
                      ) : (
                        <>Run Onboarding <ChevronRight className="h-4 w-4" /></>
                      )}
                    </button>
                  )}
                  {(step1Failed) && (
                    <button
                      onClick={() => { setOnboardJob(null); setOnboardJobId(null) }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Backfill ────────────────────────────────────────────── */}
          {step === 2 && (
            <>
              <p className="text-sm text-gray-500">
                Exports data from your source warehouse to GCS, then imports into StarRocks.
                StarRocks connection settings are carried over from Step 1.
              </p>

              {/* Warehouse type toggle */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Source Warehouse</label>
                <div className="flex gap-2">
                  {(['bigquery', 'snowflake'] as WarehouseType[]).map((wt) => (
                    <button
                      key={wt}
                      onClick={() => setWarehouseType(wt)}
                      disabled={exportRunning}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        warehouseType === wt
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'text-gray-600 border-gray-300 hover:border-orange-300 hover:text-orange-600'
                      } disabled:opacity-50`}
                    >
                      {wt === 'bigquery' ? 'BigQuery' : 'Snowflake'}
                    </button>
                  ))}
                </div>
              </div>

              {/* GCS path */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">GCS Export/Import Path</label>
                <input
                  value={gcsPath}
                  onChange={(e) => setGcsPath(e.target.value)}
                  placeholder="gs://fhin-test-starrocks/import"
                  disabled={exportRunning}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Data will be exported here and also imported from this path.
                </p>
              </div>

              {/* BigQuery fields */}
              {warehouseType === 'bigquery' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">BQ Project</label>
                    <input
                      value={bqProject}
                      onChange={(e) => setBqProject(e.target.value)}
                      placeholder="spark-bigquery-poc"
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      BQ Dataset <span className="text-gray-400">(optional, default: ACC_{accId})</span>
                    </label>
                    <input
                      value={bqDataset}
                      onChange={(e) => setBqDataset(e.target.value)}
                      placeholder={`ACC_${accId}`}
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                </div>
              )}

              {/* Snowflake fields */}
              {warehouseType === 'snowflake' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SF Account</label>
                    <input
                      value={sfAccount}
                      onChange={(e) => setSfAccount(e.target.value)}
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SF User</label>
                    <input
                      value={sfUser}
                      onChange={(e) => setSfUser(e.target.value)}
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">SF PAT Token</label>
                    <input
                      type="password"
                      value={sfPatToken}
                      onChange={(e) => setSfPatToken(e.target.value)}
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SF Database</label>
                    <input
                      value={sfDatabase}
                      onChange={(e) => setSfDatabase(e.target.value)}
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      SF Schema <span className="text-gray-400">(default: ACC_{accId})</span>
                    </label>
                    <input
                      value={sfSchema}
                      onChange={(e) => setSfSchema(e.target.value)}
                      placeholder={`ACC_${accId}`}
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">SF Stage (GCS external stage)</label>
                    <input
                      value={sfStage}
                      onChange={(e) => setSfStage(e.target.value)}
                      placeholder="@my_gcs_stage"
                      disabled={exportRunning}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                    />
                  </div>
                </div>
              )}

              {/* Job log */}
              {exportJob && (
                <JobLog job={exportJob} logRef={logRef} />
              )}

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setStep(1)}
                    disabled={exportRunning}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <StatusBadge job={exportJob} isSubmitting={exportMutation.isPending} />
                </div>
                <div className="flex gap-3">
                  {step2Done && (
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                    >
                      Done
                    </button>
                  )}
                  {!exportJob && (
                    <button
                      onClick={handleExport}
                      disabled={
                        !gcsPath ||
                        (warehouseType === 'snowflake' && (!sfAccount || !sfUser || !sfPatToken || !sfDatabase || !sfStage)) ||
                        exportMutation.isPending
                      }
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50"
                    >
                      {exportMutation.isPending ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Starting…</>
                      ) : (
                        <>Start Backfill <ChevronRight className="h-4 w-4" /></>
                      )}
                    </button>
                  )}
                  {step2Failed && (
                    <button
                      onClick={() => { setExportJob(null); setExportJobId(null) }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function JobLog({
  job,
  logRef,
}: {
  job: WarehouseJobResponse
  logRef: React.RefObject<HTMLDivElement | null>
}) {
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-600">Output</span>
        {job.status === 'RUNNING' && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-orange-500" />
        )}
      </div>
      <div
        ref={logRef}
        className="p-3 bg-gray-900 text-gray-100 text-xs font-mono h-40 overflow-y-auto space-y-0.5"
      >
        {job.logLines.length === 0 ? (
          <span className="text-gray-500">Waiting for output…</span>
        ) : (
          job.logLines.map((line, i) => (
            <div key={i} className={line.startsWith('ERROR') || line.startsWith('  ERROR') ? 'text-red-400' : ''}>
              {line}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function StatusBadge({ job, isSubmitting }: { job: WarehouseJobResponse | null; isSubmitting: boolean }) {
  if (isSubmitting) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-orange-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Starting…
      </span>
    )
  }
  if (!job) return null
  if (job.status === 'RUNNING') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-blue-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running…
      </span>
    )
  }
  if (job.status === 'COMPLETED') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-green-600">
        <CheckCircle className="h-3.5 w-3.5" /> Completed
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1.5 text-xs text-red-600">
      <XCircle className="h-3.5 w-3.5" /> Failed
    </span>
  )
}
